import React, { useId, useMemo } from 'react';
import type { ActivityViewMode } from '../../core/types/activityViewMode.types';
import type { ExploraHistoryEntry, ExploraVariable } from '../../core/types/payloads/explora.types';
import { buildExploraPriceSeries, formatExploraCurrency } from './exploraChartUtils';

export interface EconomyChartViewportProps {
  currentValue: number;
  targetValue: number;
  initialValue: number;
  unitLabel?: string;
  productLabel?: string;
  history?: ExploraHistoryEntry[];
  variables?: ExploraVariable[];
  balanced?: boolean;
  size?: 'md' | 'lg';
  mode?: ActivityViewMode;
  fill?: boolean;
}

const VB = { w: 520, h: 280 };

export function EconomyChartViewport({
  currentValue,
  targetValue,
  initialValue,
  unitLabel,
  productLabel = 'Precio',
  history = [],
  variables = [],
  balanced = false,
  size = 'md',
  mode = 'projector',
  fill = false,
}: EconomyChartViewportProps) {
  const gradId = useId();
  const series = useMemo(
    () => buildExploraPriceSeries(initialValue, history, variables),
    [initialValue, history, variables],
  );

  const values = series.map((p) => p.value);
  const minY = Math.min(initialValue, targetValue, ...values, 0) * 0.92;
  const maxY = Math.max(initialValue, targetValue, ...values, 1) * 1.08;
  const rangeY = maxY - minY || 1;

  const pad = { l: 52, r: 24, t: 36, b: 48 };
  const plotW = VB.w - pad.l - pad.r;
  const plotH = VB.h - pad.t - pad.b;

  const toX = (i: number) => pad.l + (series.length <= 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);
  const toY = (v: number) => pad.t + plotH - ((v - minY) / rangeY) * plotH;

  const linePath = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(p.value).toFixed(1)}`)
    .join(' ');

  const areaPath = `${linePath} L ${toX(series.length - 1).toFixed(1)} ${toY(minY).toFixed(1)} L ${toX(0).toFixed(1)} ${toY(minY).toFixed(1)} Z`;
  const targetY = toY(targetValue);
  const accent = balanced ? '#34d399' : '#38bdf8';

  return (
    <div
      className={`explora-economy-chart ${fill ? 'explora-economy-chart--fill' : ''}`}
      data-size={size}
      data-mode={mode}
    >
      <div className="explora-economy-chart__head">
        <div>
          <p className="explora-economy-chart__label">{productLabel}</p>
          <p className="explora-economy-chart__value" style={{ color: accent }}>
            {formatExploraCurrency(currentValue, unitLabel)}
          </p>
        </div>
        <div className="explora-economy-chart__meta">
          <span className="explora-economy-chart__meta-item">
            Meta {formatExploraCurrency(targetValue, unitLabel)}
          </span>
          <span className="explora-economy-chart__meta-item">
            Δ {currentValue - targetValue >= 0 ? '+' : ''}
            {formatExploraCurrency(currentValue - targetValue, unitLabel)}
          </span>
        </div>
      </div>

      <svg
        className="explora-economy-chart__svg"
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        role="img"
        aria-label={`Gráfico de ${productLabel}`}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
            <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Grid horizontal */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = pad.t + plotH * (1 - t);
          const val = minY + rangeY * t;
          return (
            <g key={t}>
              <line
                x1={pad.l}
                y1={y}
                x2={VB.w - pad.r}
                y2={y}
                stroke="color-mix(in srgb, var(--plugin-muted) 25%, transparent)"
                strokeDasharray="4 4"
              />
              <text
                x={pad.l - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={9}
                fill="color-mix(in srgb, var(--plugin-muted) 85%, transparent)"
              >
                {Math.round(val)}
              </text>
            </g>
          );
        })}

        {/* Línea meta */}
        <line
          x1={pad.l}
          y1={targetY}
          x2={VB.w - pad.r}
          y2={targetY}
          stroke="#f87171"
          strokeWidth={2}
          strokeDasharray="6 4"
        />
        <text
          x={VB.w - pad.r}
          y={targetY - 6}
          textAnchor="end"
          fontSize={9}
          fontWeight={700}
          fill="#f87171"
        >
          Meta
        </text>

        {/* Área + línea */}
        {series.length > 1 && (
          <>
            <path d={areaPath} fill={`url(#${gradId})`} />
            <path d={linePath} fill="none" stroke={accent} strokeWidth={3} strokeLinejoin="round" />
          </>
        )}

        {/* Puntos */}
        {series.map((p, i) => (
          <g key={`${p.label}-${i}`}>
            <circle cx={toX(i)} cy={toY(p.value)} r={i === series.length - 1 ? 6 : 4} fill={accent} />
            {i > 0 && (
              <text
                x={toX(i)}
                y={VB.h - 12}
                textAnchor="middle"
                fontSize={8}
                fill="color-mix(in srgb, var(--plugin-muted) 90%, transparent)"
              >
                {p.label.length > 10 ? `${p.label.slice(0, 9)}…` : p.label}
              </text>
            )}
          </g>
        ))}

        {series.length <= 1 && (
          <text
            x={VB.w / 2}
            y={VB.h / 2}
            textAnchor="middle"
            fontSize={12}
            fill="color-mix(in srgb, var(--plugin-muted) 70%, transparent)"
          >
            Aplica modificadores para ver la evolución del precio
          </text>
        )}
      </svg>
    </div>
  );
}
